// frontend/src/utils/mfaDefi.js
//
// Second facteur (TOTP) à la connexion du 360.
//
// ⚠️ POURQUOI CE MODULE. Le CRM partage la base Supabase (et donc les comptes) avec le
// 360. Depuis le 29/09/2026 (migrations CRM 20260929_mfa_aal2_restrictive.sql et
// 20260929_mfa_aal2_pre_request.sql, appliquées en PROD), un compte qui a un facteur MFA
// VÉRIFIÉ reçoit 401 MFA_REQUIRED sur TOUTE requête PostgREST tant que son jeton est
// `aal1`. Le 360 se connectait au mot de passe seul : la lecture de `profiles` échouait et
// l'utilisateur était renvoyé à /login sans message — impossible de se connecter.
// Ce module ne fait que PRÉSENTER le code d'un facteur déjà enrôlé (l'enrôlement se fait
// dans le CRM). Logique reprise du CRM (frontend/src/utils/exigenceMFA.js), même contrat
// @supabase/auth-js (2.95.3 installé ici : listFactors → { all, totp }, challenge → { id },
// verify → session aal2, événement MFA_CHALLENGE_VERIFIED).
//
// Module PUR : aucune dépendance à Supabase (le client est injecté), testable sans réseau.

/** Code d'erreur renvoyé par PostgREST (pre-request) et le backend CRM. */
export const CODE_MFA_REQUIS = 'MFA_REQUIRED';

/**
 * Claim `aal` d'un jeton d'accès (lecture seule, pour décider d'un AFFICHAGE : la
 * vérification de signature appartient au serveur). `null` si illisible.
 * @param {string|null|undefined} jeton
 * @returns {string|null}
 */
export function aalDuJeton(jeton) {
  try {
    const charge = String(jeton || '').split('.')[1];
    if (!charge) return null;
    const b64 = charge.replace(/-/g, '+').replace(/_/g, '/');
    const json = JSON.parse(atob(b64 + '='.repeat((4 - (b64.length % 4)) % 4)));
    return typeof json?.aal === 'string' ? json.aal : null;
  } catch {
    return null;
  }
}

/**
 * Niveaux d'assurance calculés depuis une session déjà en main (même calcul que
 * GoTrueClient._getAuthenticatorAssuranceLevel) : currentLevel = claim `aal` du jeton ;
 * nextLevel = 'aal2' s'il existe un facteur vérifié, sinon currentLevel.
 * @param {{access_token?: string, user?: {factors?: Array<{status?: string}>}}|null} session
 * @returns {{currentLevel: string|null, nextLevel: string|null}}
 */
export function niveauxDeSession(session) {
  if (!session) return { currentLevel: null, nextLevel: null };
  const currentLevel = aalDuJeton(session.access_token);
  const facteurs = Array.isArray(session.user?.factors) ? session.user.factors : [];
  const nextLevel = facteurs.some((f) => f?.status === 'verified') ? 'aal2' : currentLevel;
  return { currentLevel, nextLevel };
}

/**
 * La session doit-elle présenter un second facteur avant tout accès ?
 * Volontairement étroite : seul aal1 → aal2 déclenche le défi (un compte sans facteur,
 * ou dont le facteur a été retiré, n'est jamais envoyé vers un défi impossible).
 * @param {{currentLevel?: string|null, nextLevel?: string|null}} niveaux
 * @returns {boolean}
 */
export function defiEnAttente(niveaux) {
  return niveaux?.nextLevel === 'aal2' && niveaux?.currentLevel !== 'aal2';
}

/**
 * Facteurs TOTP vérifiés à qui présenter le code, dans l'ordre d'essai (tous, pas le
 * premier : l'appareil de secours doit pouvoir servir à se connecter).
 * `data.totp` est repris tel quel (déjà filtré par le client) ; `data.all` est relu
 * positivement (`status === 'verified'`) pour rattraper un facteur omis.
 * @param {{all?: unknown, totp?: unknown}} [donnees] champ `data` de listFactors()
 * @returns {Array<{id: string, friendly_name?: string}>}
 */
export function facteursPourLeDefi(donnees) {
  const depuisTotp = Array.isArray(donnees?.totp) ? donnees.totp : [];
  const depuisAll = (Array.isArray(donnees?.all) ? donnees.all : []).filter(
    (f) => f?.factor_type === 'totp' && f?.status === 'verified'
  );
  const vus = new Set();
  const retenus = [];
  for (const facteur of [...depuisTotp, ...depuisAll]) {
    const id = facteur?.id;
    if (typeof id !== 'string' || id.length === 0 || vus.has(id)) continue;
    vus.add(id);
    retenus.push(facteur);
  }
  return retenus;
}

// Les seuls codes d'erreur qui parlent du CODE SAISI (AuthError.code). Tout le reste
// (défi expiré, débit, réseau…) est technique : ne pas accuser l'horloge du téléphone.
const CODES_DE_REFUS_DU_CODE = new Set(['mfa_verification_failed', 'mfa_verification_rejected']);

/** @param {{code?: string}} [erreur] @returns {boolean} */
export function echecImputableAuCode(erreur) {
  const code = erreur?.code;
  return typeof code === 'string' && CODES_DE_REFUS_DU_CODE.has(code);
}

function texteDeLErreur(erreur) {
  const brut = erreur?.message;
  if (typeof brut === 'string' && brut.trim().length > 0) return brut.trim();
  if (typeof erreur === 'string' && erreur.trim().length > 0) return erreur.trim();
  return 'cause non précisée par le serveur';
}

/**
 * Message quand AUCUN facteur n'a accepté le code. N'accuse le code que si TOUS les
 * échecs lui sont imputables.
 * @param {Array<unknown>} echecs
 * @returns {string}
 */
export function messageDefiEchoue(echecs) {
  const liste = Array.isArray(echecs) ? echecs : [];
  if (liste.length === 0) {
    return 'Votre code n’a pas pu être vérifié : aucun appareil enrôlé n’a pu être interrogé. '
      + 'Activez la double authentification depuis le CRM, ou prévenez l’administrateur.';
  }
  const technique = liste.find((e) => !echecImputableAuCode(e));
  if (technique !== undefined) {
    return `Votre code n’a pas pu être vérifié : ${texteDeLErreur(technique)}. `
      + 'Réessayez ; si le problème persiste, prévenez l’administrateur.';
  }
  if (liste.length === 1) {
    return `Code refusé : ${texteDeLErreur(liste[0])}. Vérifiez l’heure de votre téléphone, `
      + 'puis retentez avec le code affiché à cet instant.';
  }
  return `Code refusé : aucun de vos ${liste.length} appareils enrôlés n’a accepté ce code. `
    + 'Vérifiez l’heure du téléphone qui l’affiche, puis retentez avec le code affiché à cet instant.';
}

/**
 * Présente le code à six chiffres à CHAQUE facteur vérifié, jusqu'au premier succès.
 * Aucun échec n'interrompt la boucle (une panne sur le facteur n°1 ne doit pas masquer un
 * succès sur le n°2). Le client MFA est injecté.
 * @param {{mfa: {challenge: Function, verify: Function}, facteurs: Array<{id: string}>, code: string}} args
 * @returns {Promise<{succes: true, facteur: object} | {succes: false, echecs: unknown[]}>}
 */
export async function presenterLeCodeSurChaqueFacteur({ mfa, facteurs, code }) {
  const liste = Array.isArray(facteurs) ? facteurs : [];
  const echecs = [];
  for (const facteur of liste) {
    try {
      const { data: defi, error: erreurDefi } = await mfa.challenge({ factorId: facteur.id });
      if (erreurDefi) throw erreurDefi;
      if (!defi?.id) throw new Error('défi sans identifiant renvoyé par le serveur');
      const { error: erreurVerif } = await mfa.verify({ factorId: facteur.id, challengeId: defi.id, code });
      if (erreurVerif) throw erreurVerif;
      return { succes: true, facteur };
    } catch (err) {
      echecs.push(err);
    }
  }
  return { succes: false, echecs };
}

/** Le code saisi a-t-il la forme attendue (6 chiffres) ? */
export function codeBienForme(code) {
  return /^\d{6}$/.test(String(code || '').trim());
}
